import Vue from "vue";
import { mapGetters } from "vuex";
import { LoadingSpinner } from "@/components";
import { themeHelper } from "@/components/mixins/themeHelper";
import { constants } from "@/const";
import DiagramViewer from "@/components/PartDiagramSearch/DiagramViewer/DiagramViewer.vue";
import DiagramPartsList from "@/components/PartDiagramSearch/DiagramPartsList/DiagramPartsList.vue";
import PartDetailRow from "@/components/PartDiagramSearch/PartDetailRow/PartDetailRow.vue";
import PaginationText from "@/components/Elements/PaginationText.vue";
import Pagination from "@/components/Elements/Pagination/Pagination.vue";
import PartSearchFacets from "@/components/SubMenus/PartSearchFacets.vue";
import SkeletonLoader from "@/components/Elements/SkeletonLoader.vue";
import { DnRouterLink } from "@/components/Elements";
import SvgDiagram from "@/components/PartDiagramSearch/PartSearchResultsPanel/SvgDiagram.vue";
import { OemManualsService, IOemManualsDiagramEntry } from "@/services/OemManualsService";

/** Landscape shows the diagram at a fixed zoom rather than fitting it to the shorter strip. */
const LANDSCAPE_DEFAULT_ZOOM = 0.75;

/** Tolerance for comparing diagramZoom against portraitDefaultZoom, to absorb floating-point drift. */
const ZOOM_EPSILON = 0.01;

/** Key of the component's own diagram; sub-assembly diagrams use OemManualsService.SubAssemblyDiagramKey. */
const MAIN_DIAGRAM_KEY = "main";

/**
 * PartSearchResultsPanel – Renders the right-hand results area of the Part
 * Diagram page. Supports two layout modes:
 *
 *  1. **Diagram mode** (`showDiagram = true`) – side-by-side DiagramViewer
 *     and DiagramPartsList.
 *  2. **List mode** (`showDiagram = false`) – traditional paginated
 *     PartDetailRow list with sort, compact/detailed toggle, and alt-part legend.
 */
export default Vue.extend({
    name: "PartSearchResultsPanel",
    components: {
        LoadingSpinner,
        DiagramViewer,
        DiagramPartsList,
        PartDetailRow,
        PaginationText,
        Pagination,
        PartSearchFacets,
        SkeletonLoader,
        DnRouterLink,
        SvgDiagram,
    },
    mixins: [
        themeHelper
    ],
    props: {
        /** Whether the results are still loading. */
        loading: {
            type: Boolean,
            default: false
        },
        /** Search results object containing `Parts`, `Facets`, etc. */
        searchResults: {
            type: Object,
            default: () => ({})
        },
        /** Whether any returned parts have alternate-part tabs. */
        hasAltParts: {
            type: Boolean,
            default: false
        },
        /** URL (or data-URI) of the diagram image. */
        diagramUrl: {
            type: String,
            default: ""
        },
        /** Alt text for the diagram image. */
        diagramAltText: {
            type: String,
            default: ""
        },
        /** Whether search results contain parts. */
        hasResults: {
            type: Boolean,
            default: false
        },
        /** When true, renders diagram+cards layout instead of list layout. */
        showDiagram: {
            type: Boolean,
            default: true
        },
        /** Total number of parts across all pages. */
        totalParts: {
            type: Number,
            default: 0
        },
        /** Number of parts per page in list mode. */
        partsPerPage: {
            type: Number,
            default: 10
        },
        /** Initial page number (from deep-link or parent). */
        initialPageNumber: {
            type: Number,
            default: 1
        },
        /** Currently selected facets from the parent page. */
        selectedFacets: {
            type: Object,
            default: () => ({})
        },
        /** Active search term text. */
        searchTerm: {
            type: String,
            default: ""
        },
        /** Whether OemManuals mode is active (client-side pagination). */
        isDiagramSearch: {
            type: Boolean,
            default: false
        },
        /** SVG diagram data from OemManuals search. */
        diagramSvgData: {
            type: String,
            default: ""
        },
        /** Whether the diagram for the newly selected component is still loading. */
        diagramLoading: {
            type: Boolean,
            default: false
        },
        /** Title shown above the diagram — the selected component's name. */
        diagramTitle: {
            type: String,
            default: ""
        },
        /** Sub-assembly diagrams the user can switch to alongside the main diagram. */
        subAssemblyDiagrams: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        ...mapGetters([
            "getContentItem",
            "partSearchDefaultSortValue",
            "tmsCompactViewDefaultValue",
            "diagramOrientationDefaultValue"
        ]),
        /** Key that changes when the active component/diagram changes. */
        diagramContextKey(): string {
            return [
                this.searchResults?.OemDescription || "",
                this.searchResults?.Model || "",
                this.selectedFacets?.Category || "",
                this.selectedFacets?.ComponentGroup || "",
                this.selectedFacets?.Component || "",
                this.diagramSvgData ? `svg:${this.diagramSvgData.length}` : "",
                this.diagramUrl ? `url:${this.diagramUrl.length}` : "",
            ].join("|");
        },
        /** Effective total: in OemManuals mode use the full Parts array length. */
        effectiveTotalParts(): number {
            if (this.isDiagramSearch && this.searchResults?.Parts) {
                return this.searchResults.Parts.length;
            }
            return this.totalParts;
        },
        /** Total pages based on effective total. */
        effectiveTotalPages(): number {
            return Math.ceil(this.effectiveTotalParts / this.partsPerPage);
        },
        /** Parts to display: full array for both OemManuals (non-paginated) and TMS (server-paginated). */
        displayedParts(): any[] {
            return this.partGroups.parts;
        },
        /**
         * In diagram search, lists each sub-assembly's parts directly after their assembly header
         * part, and counts them per SubAssemblyArtId. Parts whose header isn't in the results keep
         * their original position.
         */
        partGroups(): { parts: any[]; childCounts: Record<number, number>; groups: Array<{ part: any; subParts: any[] }> } {
            const parts: any[] = this.searchResults?.Parts || [];
            const childCounts: Record<number, number> = {};
            if (!this.isDiagramSearch) {
                return { parts, childCounts, groups: parts.map((p: any) => ({ part: p, subParts: [] })) };
            }

            const headerArtIds: Record<number, boolean> = {};
            parts.forEach((p: any) => {
                if (OemManualsService.IsAssemblyHeader(p)) {
                    headerArtIds[p.SubAssemblyArtId] = true;
                }
            });
            const isGroupedChild = (p: any) =>
                !OemManualsService.IsAssemblyHeader(p) && !!headerArtIds[p.SubAssemblyArtId];

            const ordered: any[] = [];
            const groups: Array<{ part: any; subParts: any[] }> = [];
            parts.forEach((p: any) => {
                if (isGroupedChild(p)) return;
                ordered.push(p);

                const artId = p.SubAssemblyArtId;
                // The `in` check attaches children to the first header only, should the data ever repeat one
                if (OemManualsService.IsAssemblyHeader(p) && !(artId in childCounts)) {
                    const children = parts.filter((c: any) => isGroupedChild(c) && c.SubAssemblyArtId === artId);
                    ordered.push(...children);
                    childCounts[artId] = children.length;
                    groups.push({ part: p, subParts: children });
                } else {
                    groups.push({ part: p, subParts: [] });
                }
            });
            return { parts: ordered, childCounts, groups };
        },
        /** ItemNumbers on the active diagram with a corresponding part, used to grey out unserviceable hotspots. */
        validItemNumbers(): Array<string | number> {
            if (!this.isDiagramSearch || !this.searchResults?.Parts) return [];
            return this.searchResults.Parts
                .filter((p: any) => p.ItemNumber != null && this.diagramKeyForPart(p) === this.activeDiagramKey)
                .map((p: any) => p.ItemNumber);
        },
        /** The main component diagram followed by any sub-assembly diagrams; the header's prev/next steps through these. */
        diagrams(): IOemManualsDiagramEntry[] {
            const main: IOemManualsDiagramEntry = {
                key: MAIN_DIAGRAM_KEY,
                title: this.diagramTitle || "Component Diagram",
                svgData: this.diagramSvgData,
                loading: this.diagramLoading
            };
            return [main, ...(this.subAssemblyDiagrams as IOemManualsDiagramEntry[])];
        },
        activeDiagram(): IOemManualsDiagramEntry {
            return this.diagrams[this.activeDiagramIndex] || this.diagrams[0];
        },
        activeDiagramKey(): string {
            return this.activeDiagram.key;
        },
        /** The highlighted ItemNumber, but only while its diagram is the one being shown. */
        activeHighlightedItemNumber(): string | number | null {
            return this.highlightedDiagramKey === this.activeDiagramKey ? this.highlightedItemNumber : null;
        },
        /** True only when an actual diagram (SVG or image) was found for the current search. */
        hasDiagram(): boolean {
            return !!(this.diagramSvgData);
        },
        /** True when the diagram pane should render (loaded diagram, or one still loading). */
        hasStickyDiagram(): boolean {
            return this.isDiagramSearch && !!(this.diagramSvgData || this.diagramLoading);
        },
        /**
         * True when, in portrait mode, the user has zoomed in past the diagram's
         * fitted default. Reference-number clicks then pan/scroll like landscape
         * does; at or below the default the diagram stays static.
         */
        isPortraitZoomedIn(): boolean {
            return this.diagramOrientation === "portrait"
                && this.portraitDefaultZoom != null
                && this.diagramZoom > this.portraitDefaultZoom + ZOOM_EPSILON;
        },
    },
    data: () => ({
        /** Whether the condensed (compact) part view is active. */
        compactView: false,
        pageNumber: 1,
        selectedSortTerm: "",
        sortByOptions: constants.SORT_BY_OPTIONS,
        inStockFilter: false,
        /** Whether the sticky diagram is expanded. */
        diagramExpanded: true,
        /** Diagram layout: "portrait" (beside the parts) or "landscape" (strip above them). */
        diagramOrientation: "portrait" as "portrait" | "landscape",
        /** Current zoom level for the sticky diagram. */
        diagramZoom: LANDSCAPE_DEFAULT_ZOOM,
        /** Portrait's last fit-to-viewport zoom, used to tell whether the user has since zoomed in. */
        portraitDefaultZoom: null as number | null,
        /** Last manual viewport height used before the diagram was hidden. */
        savedDiagramViewportHeight: "",
        /** ItemNumber of the part currently highlighted via a diagram hotspot click. */
        highlightedItemNumber: null as string | number | null,
        /** Key of the diagram that highlightedItemNumber belongs to (ItemNumbers repeat across diagrams). */
        highlightedDiagramKey: null as string | null,
        /** Index into `diagrams` of the diagram currently shown. */
        activeDiagramIndex: 0,
        /** SubAssemblyArtIds whose parts are collapsed under their assembly part (array for Vue 2 reactivity). */
        collapsedAssemblyArtIds: [] as number[],
        /** Whether the alternate-part code legend is expanded. */
        showAltPartLegend: false,
    }),
    created() {
        this.pageNumber = this.initialPageNumber || 1;
        this.compactView = this.tmsCompactViewDefaultValue === "true" || this.tmsCompactViewDefaultValue === true;
        this.diagramOrientation = this.diagramOrientationDefaultValue === "landscape" ? "landscape" : "portrait";

        // NOTE: The parent (PartDiagramPage) triggers the initial search via
        // getCategoryParts / loadOemManualsDiagram in onSearchComplete, so we
        // intentionally do NOT call performPartSearch here to avoid a duplicate
        // API call on first render.
    },
    watch: {
        /** Persist compact-view preference whenever the user toggles it. */
        compactView(newVal: boolean) {
            this.$store.commit("SET_TMS_COMPACT_VIEW", newVal);
        },
        /** Persist the orientation preference whenever the user switches layouts. */
        diagramOrientation(newVal: string) {
            if (newVal === this.diagramOrientationDefaultValue) return;
            this.$store.commit("SET_DIAGRAM_ORIENTATION", newVal);
        },
        /** Sync page number when the parent signals a new page (new search or pagination). */
        initialPageNumber(newVal: number) {
            this.pageNumber = newVal || 1;
        },
        /** Reset zoom, pan, diagram selection and collapsed groups whenever the active component changes. */
        diagramContextKey() {
            this.activeDiagramIndex = 0;
            this.collapsedAssemblyArtIds = [];
            this.resetDiagramView();
        },
        /** Guards against a stale index when the sub-assembly list is replaced without a context change. */
        "subAssemblyDiagrams.length"() {
            if (this.activeDiagramIndex >= this.diagrams.length) {
                this.activeDiagramIndex = 0;
            }
        }
    },
    methods: {
        /** Bubble the hasAltParts event to the parent page. */
        HasAltParts(emittedHasAlts: boolean) {
            this.$emit("hasAltParts", emittedHasAlts);
        },

        /** Forward the facet-update event to the parent page. */
        UpdateFacets() {
            this.$emit("updateFacets");
        },

        /** Forward the redirect-to-truck-select event to the parent page. */
        redirectToTruckSelect() {
            this.$emit("redirectToTruckSelect");
        },

        /** Triggers a new part search for the given page. In OemManuals mode,
         *  pagination is client-side so we just update the page number.
         *  In TMS mode, emits to the parent for a server-side fetch. */
        performPartSearch(page: number) {
            this.pageNumber = page;
            if (this.isDiagramSearch) {
                // Client-side pagination — displayedParts computed will react
                return;
            }
            if (this.selectedSortTerm === "") {
                this.selectedSortTerm = this.partSearchDefaultSortValue === "" ? "salesVolumeDesc" : this.partSearchDefaultSortValue;
            }
            const selectedSortOption = this.sortByOptions.find((option: any) => option.value === this.selectedSortTerm);
            this.$emit("partSearch", selectedSortOption?.sortKey, selectedSortOption?.sortDirection, this.pageNumber, this.inStockFilter);
        },

        /** Builds a URL with the given category set as a query parameter. */
        getCategoryUrl(category: string): string {
            const url = new URL(window.location.href);
            url.searchParams.set("Category", category);
            return url.pathname + url.search;
        },

        /** Handles mouse wheel zoom on the diagram. */
        handleDiagramWheel(event: WheelEvent) {
            event.preventDefault();

            // Use Ctrl+Scroll or just scroll to zoom
            const zoomDelta = event.deltaY > 0 ? -0.1 : 0.1;
            this.zoomDiagramBy(zoomDelta, event.clientX, event.clientY);
        },

        /** Zooms the diagram around the given point, or the viewport center when no point is supplied. */
        zoomDiagramBy(delta: number, clientX?: number, clientY?: number) {
            const nextZoom = Math.max(0.1, Math.min(3, this.diagramZoom + delta));
            const svgDiagram = this.$refs.svgDiagram as any;
            const diagramContent = this.$refs.diagramContent as HTMLElement | undefined;

            if (svgDiagram?.zoomToPoint) {
                const viewportRect = diagramContent?.getBoundingClientRect();
                const targetX = clientX ?? (viewportRect ? viewportRect.left + viewportRect.width / 2 : 0);
                const targetY = clientY ?? (viewportRect ? viewportRect.top + viewportRect.height / 2 : 0);
                svgDiagram.zoomToPoint(nextZoom, targetX, targetY);
            }

            this.diagramZoom = nextZoom;
        },

        /** Resets zoom, recenters the diagram, and clears any inline width/height the native resize handle applied. */
        resetZoomAndSize() {
            if (this.diagramOrientation === "landscape") {
                this.diagramZoom = LANDSCAPE_DEFAULT_ZOOM;
            }
            this.resetDiagramView();
            const el = this.$refs.diagramContent as HTMLElement | undefined;
            if (el) {
                el.style.width = "";
                el.style.height = "";
            }
            const wrapper = (this.$el as HTMLElement | null)?.querySelector(".diagram-results-panel__sticky-diagram-wrapper") as HTMLElement | null;
            if (wrapper) {
                wrapper.style.height = "";
            }
        },

        /** Stores the last visible height so it can be restored after the diagram is hidden. */
        saveDiagramViewportHeight() {
            const wrapper = (this.$el as HTMLElement | null)?.querySelector(".diagram-results-panel__sticky-diagram-wrapper") as HTMLElement | null;
            if (!wrapper) return;

            const height = wrapper.getBoundingClientRect().height;
            if (height > 0) {
                this.savedDiagramViewportHeight = `${Math.round(height)}px`;
            }
        },

        /** Restores the last saved height for the diagram viewport when it is shown again. */
        restoreDiagramViewportHeight() {
            const wrapper = (this.$el as HTMLElement | null)?.querySelector(".diagram-results-panel__sticky-diagram-wrapper") as HTMLElement | null;
            if (!wrapper || !this.savedDiagramViewportHeight) return;

            wrapper.style.height = this.savedDiagramViewportHeight;
            wrapper.style.minHeight = "";
        },

        /** Toggles the diagram visibility while preserving the last viewport height for restore. */
        toggleDiagramExpanded() {
            const wrapper = (this.$el as HTMLElement | null)?.querySelector(".diagram-results-panel__sticky-diagram-wrapper") as HTMLElement | null;

            if (this.diagramExpanded && wrapper) {
                this.saveDiagramViewportHeight();
            }

            this.diagramExpanded = !this.diagramExpanded;

            if (!this.diagramExpanded) {
                if (wrapper) {
                    wrapper.style.height = "";
                    wrapper.style.minHeight = "0";
                }

                const content = this.$refs.diagramContent as HTMLElement | undefined;
                if (content) {
                    content.style.height = "";
                }
                return;
            }

            this.restoreDiagramViewportHeight();
        },

        /** Resets zoom and pan to the default centered view. */
        resetDiagramView() {
            this.highlightedItemNumber = null;
            this.highlightedDiagramKey = null;
            this.$nextTick(() => {
                const svgDiagram = this.$refs.svgDiagram as any;
                if (svgDiagram?.resetPan) {
                    svgDiagram.resetPan();
                }
            });
        },

        /** Switches between the portrait (beside the parts) and landscape (above them) layouts. */
        setDiagramOrientation(orientation: "portrait" | "landscape") {
            if (this.diagramOrientation === orientation) return;

            this.diagramOrientation = orientation;

            // Landscape keeps a fixed default zoom; portrait fits the diagram to its taller pane.
            if (orientation === "landscape") {
                this.diagramZoom = LANDSCAPE_DEFAULT_ZOOM;
            }

            // Any manually resized height belongs to the orientation being left behind.
            this.savedDiagramViewportHeight = "";
            const wrapper = (this.$el as HTMLElement | null)?.querySelector(".diagram-results-panel__sticky-diagram-wrapper") as HTMLElement | null;
            if (wrapper) {
                wrapper.style.height = "";
                wrapper.style.minHeight = "";
            }
            const content = this.$refs.diagramContent as HTMLElement | undefined;
            if (content) {
                content.style.width = "";
                content.style.height = "";
            }

            this.$nextTick(() => {
                const svgDiagram = this.$refs.svgDiagram as any;
                if (svgDiagram?.resetPan) {
                    svgDiagram.resetPan();
                }
            });
        },

        /**
         * Keeps the zoom % readout in sync when SvgDiagram fits the diagram to the
         * viewer. This only fires from a fit-to-viewport, which only happens in
         * portrait mode, so it also records the new portrait default zoom.
         */
        onDiagramZoomChange(zoom: number) {
            this.diagramZoom = zoom;
            if (this.diagramOrientation === "portrait") {
                this.portraitDefaultZoom = zoom;
            }
        },

        /**
         * Handles a click on a diagram hotspot (SvgDiagram emits the hotspot's
         * `name` attribute, which matches a part's ItemNumber). Highlights the
         * hotspot, centers it without changing zoom (landscape only), and scrolls its part row into view.
         */
        async onHotspotClick(itemNumber: string) {
            const diagramKey = this.activeDiagramKey;
            const part = this.displayedParts.find((p: any) =>
                p.ItemNumber != null
                && String(p.ItemNumber) === itemNumber
                && this.diagramKeyForPart(p) === diagramKey
            );

            this.highlightedDiagramKey = diagramKey;
            this.highlightedItemNumber = itemNumber;

            if (part) {
                this.expandAssemblyFor(part);
            }

            await this.$nextTick();

            // Portrait shows the whole diagram, so the highlight alone is enough —
            // unless the user has zoomed in past the fitted default.
            if (this.diagramOrientation !== "portrait" || this.isPortraitZoomedIn) {
                const svgDiagram = this.$refs.svgDiagram as any;
                if (svgDiagram?.focusHotspot) {
                    await svgDiagram.focusHotspot(itemNumber);
                }
            }

            this.scrollPartRowIntoView(diagramKey, itemNumber);
        },

        // ─── Sub-assembly grouping ──────────────────────────────────────

        /** Key of the diagram on which the given part's ItemNumber appears as a hotspot. */
        diagramKeyForPart(part: any): string {
            if (part?.SubAssemblyArtId != null && !OemManualsService.IsAssemblyHeader(part)) {
                return OemManualsService.SubAssemblyDiagramKey(part.SubAssemblyArtId);
            }
            return MAIN_DIAGRAM_KEY;
        },

        /** True for a part listed (indented) under its assembly header part. */
        isSubAssemblyPart(part: any): boolean {
            return part?.SubAssemblyArtId != null
                && !OemManualsService.IsAssemblyHeader(part)
                && !!this.partGroups.childCounts[part.SubAssemblyArtId];
        },

        isAssemblyCollapsed(artId: number): boolean {
            return this.collapsedAssemblyArtIds.indexOf(artId) >= 0;
        },

        toggleAssemblyCollapsed(artId: number) {
            const index = this.collapsedAssemblyArtIds.indexOf(artId);
            if (index >= 0) {
                this.collapsedAssemblyArtIds.splice(index, 1);
            } else {
                this.collapsedAssemblyArtIds.push(artId);
            }
        },

        /** True for a sub-assembly part hidden because its group is collapsed. */
        isPartCollapsedAway(part: any): boolean {
            return this.isSubAssemblyPart(part) && this.isAssemblyCollapsed(part.SubAssemblyArtId);
        },

        /** Expands the group containing the given part so its row can be shown and scrolled to. */
        expandAssemblyFor(part: any) {
            if (this.isPartCollapsedAway(part)) {
                this.toggleAssemblyCollapsed(part.SubAssemblyArtId);
            }
        },

        // ─── Diagram switching ──────────────────────────────────────────

        /** Shows the diagram at the given index, expanding the diagram pane if it was hidden. */
        selectDiagram(index: number) {
            if (index < 0 || index >= this.diagrams.length) return;
            this.activeDiagramIndex = index;
            if (!this.diagramExpanded) {
                this.diagramExpanded = true;
                this.restoreDiagramViewportHeight();
            }
        },

        selectDiagramByKey(key: string) {
            this.selectDiagram(this.diagrams.findIndex((d) => d.key === key));
        },

        showPrevDiagram() {
            this.selectDiagram(this.activeDiagramIndex - 1);
        },

        showNextDiagram() {
            this.selectDiagram(this.activeDiagramIndex + 1);
        },

        // ─── Part row lookup / scrolling ────────────────────────────────

        /**
         * Scrolls the given part row into view, offsetting for the sticky
         * diagram's height so the row (and its FullPartDetails) aren't left
         * hidden underneath it.
         */
        scrollPartRowIntoView(diagramKey: string, itemNumber: string | number) {
            const rowEl = document.getElementById(this.partRowId(diagramKey, itemNumber));
            if (!rowEl) return;

            const root = this.$el as HTMLElement;
            const sideBySide = this.isSideBySideLayout();
            const stickyEl = root.querySelector(".diagram-results-panel__sticky-diagram-wrapper") as HTMLElement | null;
            const stickyHeight = stickyEl ? stickyEl.getBoundingClientRect().height : 0;

            const breathingRoom = 16;
            const rowTop = rowEl.getBoundingClientRect().top + window.scrollY;

            // In the side-by-side layout the diagram sits beside (not above) the
            // rows, so no vertical offset is needed.
            let target = sideBySide ? rowTop - breathingRoom : rowTop - stickyHeight - breathingRoom;

            if (sideBySide) {
                // The diagram unsticks once its pane's bottom edge reaches the viewport, so rows near
                // the end of the list would otherwise scroll the diagram off the top of the screen.
                const pane = root.querySelector(".diagram-results-panel__diagram-pane") as HTMLElement | null;
                if (pane) {
                    const paneBottom = pane.getBoundingClientRect().bottom + window.scrollY;
                    target = Math.min(target, paneBottom - stickyHeight);
                }
            }

            window.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
        },

        /** True when the diagram and results are rendered side by side (wide viewports). */
        isSideBySideLayout(): boolean {
            return this.hasStickyDiagram
                && this.diagramExpanded
                && this.diagramOrientation === "portrait"
                && window.innerWidth > 991;
        },

        /** Builds the DOM id used to locate/scroll to a part row by its diagram and ItemNumber. */
        partRowId(diagramKey: string, itemNumber: string | number): string {
            return `diagram-part-row-${diagramKey}-${itemNumber}`;
        },

        /** True when the given part matches the currently highlighted hotspot on its own diagram. */
        isPartHighlighted(part: any): boolean {
            return (
                this.highlightedItemNumber != null &&
                part?.ItemNumber != null &&
                this.highlightedDiagramKey === this.diagramKeyForPart(part) &&
                String(part.ItemNumber) === String(this.highlightedItemNumber)
            );
        },

        /**
         * Handles a click on a part's ItemNumber badge: switches to the diagram the
         * part belongs to, then pans to and highlights its hotspot without changing zoom.
         * An assembly header's hotspot is on the main diagram, so its badge goes there.
         */
        async onItemNumberClick(part: any) {
            const itemNumber = part?.ItemNumber;
            if (itemNumber == null) return;

            const preservedZoom = this.diagramZoom;

            const diagramKey = this.diagramKeyForPart(part);
            this.highlightedDiagramKey = diagramKey;
            this.highlightedItemNumber = itemNumber;

            const targetIndex = this.diagrams.findIndex((d) => d.key === diagramKey);
            if (targetIndex < 0) return;
            const switching = targetIndex !== this.activeDiagramIndex;
            this.selectDiagram(targetIndex);

            if (switching) {
                // Let SvgDiagram finish loading the new SVG, then restore the
                // user's zoom because selecting a part must only pan the view.
                await this.$nextTick();
                await this.$nextTick();
                this.diagramZoom = preservedZoom;
                await this.$nextTick();
            }

            // Portrait shows the whole diagram, so the highlight alone is enough —
            // unless the user has zoomed in past the fitted default.
            if (this.diagramOrientation === "portrait" && !this.isPortraitZoomedIn) return;

            const svgDiagram = this.$refs.svgDiagram as any;
            if (svgDiagram?.focusHotspot) {
                await svgDiagram.focusHotspot(itemNumber);
            }

            const diagramWrapper = (this.$el as HTMLElement).querySelector(".diagram-results-panel__sticky-diagram-wrapper");
            if (diagramWrapper && !this.isSideBySideLayout()) {
                diagramWrapper.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }
    }
});
